'use client';
import { useRef, useState } from 'react';

/* ─── Upload Section ─────────────────────────────────────────────────────── */
export const UploadSection = ({ onFileSelect, loading }) => {
    const inputRef   = useRef(null);
    const [drag, setDrag]   = useState(false);
    const [file, setFile]   = useState(null);
    const [error, setError] = useState('');

    const validate = (f) => {
        if (!f) return false;
        if (f.type !== 'application/pdf') {
            setError('Only PDF files are accepted. Please upload a valid invoice PDF.');
            return false;
        }
        if (f.size > 10 * 1024 * 1024) {
            setError('File size must be under 10 MB.');
            return false;
        }
        setError('');
        return true;
    };

    const handleFile = (f) => {
        if (!validate(f)) return;
        setFile(f);
        onFileSelect(f);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDrag(false);
        const f = e.dataTransfer.files?.[0];
        if (f) handleFile(f);
    };

    const handleChange = (e) => {
        const f = e.target.files?.[0];
        if (f) handleFile(f);
    };

    const handleRemove = (e) => {
        e.stopPropagation();
        setFile(null);
        setError('');
        onFileSelect(null);
        if (inputRef.current) inputRef.current.value = '';
    };

    return (
        <div className="flex flex-column align-items-center gap-3">
            {/* ── Drop Zone ── */}
            <div
                onClick={() => !file && inputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); if (!file) setDrag(true); }}
                onDragLeave={() => setDrag(false)}
                onDrop={handleDrop}
                style={{
                    width: '100%',
                    maxWidth: '560px',
                    minHeight: file ? 'auto' : '190px',
                    border: `2px dashed ${drag ? '#10b981' : file ? '#10b98160' : '#d1d5db'}`,
                    borderRadius: '1rem',
                    background: drag
                        ? 'rgba(16,185,129,0.06)'
                        : file
                            ? 'rgba(16,185,129,0.04)'
                            : 'var(--surface-ground)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: file ? 'default' : 'pointer',
                    padding: '1.5rem',
                    transition: 'all 0.25s ease',
                    boxSizing: 'border-box',
                }}
            >
                {!file ? (
                    /* ─ Empty state ─ */
                    <div className="flex flex-column align-items-center gap-3 text-center">
                        <div
                            className="flex align-items-center justify-content-center border-circle"
                            style={{
                                width: 64, height: 64,
                                background: drag
                                    ? 'linear-gradient(135deg, #10b981, #1bb8a7)'
                                    : 'rgba(16,185,129,0.1)',
                                border: `1.5px solid ${drag ? '#10b981' : 'rgba(16,185,129,0.25)'}`,
                                transition: 'all 0.25s',
                            }}
                        >
                            <i
                                className="pi pi-cloud-upload"
                                style={{
                                    fontSize: '1.6rem',
                                    color: drag ? '#fff' : '#10b981',
                                    transition: 'color 0.25s',
                                }}
                            />
                        </div>

                        <div>
                            <p className="m-0 font-bold" style={{ fontSize: '1rem', color: 'var(--text-color)' }}>
                                {drag ? 'Drop your invoice here' : 'Upload your invoice PDF'}
                            </p>
                            <p className="m-0 mt-1 text-500" style={{ fontSize: '0.82rem' }}>
                                Drag & drop or click to browse
                            </p>
                        </div>

                        {/* Constraints */}
                        <div className="flex align-items-center gap-3 flex-wrap justify-content-center">
                            <span
                                className="flex align-items-center gap-1"
                                style={{
                                    fontSize: '0.72rem',
                                    color: '#10b981',
                                    background: '#f0fdf4',
                                    border: '1px solid #10b98130',
                                    borderRadius: '999px',
                                    padding: '0.2rem 0.6rem',
                                    fontWeight: 600,
                                }}
                            >
                                <i className="pi pi-file-pdf" style={{ fontSize: '0.65rem' }} />
                                PDF only
                            </span>
                            <span
                                style={{
                                    fontSize: '0.72rem',
                                    color: '#6b7280',
                                    background: 'var(--surface-card)',
                                    border: '1px solid var(--surface-border)',
                                    borderRadius: '999px',
                                    padding: '0.2rem 0.6rem',
                                    fontWeight: 500,
                                }}
                            >
                                Max 10 MB
                            </span>
                            <span
                                style={{
                                    fontSize: '0.72rem',
                                    color: '#6b7280',
                                    background: 'var(--surface-card)',
                                    border: '1px solid var(--surface-border)',
                                    borderRadius: '999px',
                                    padding: '0.2rem 0.6rem',
                                    fontWeight: 500,
                                }}
                            >
                                Amazon / Flipkart
                            </span>
                        </div>
                    </div>
                ) : (
                    /* ─ File selected state ─ */
                    <div
                        className="flex align-items-center justify-content-between gap-3"
                        style={{ width: '100%' }}
                    >
                        <div className="flex align-items-center gap-3">
                            <div
                                className="flex align-items-center justify-content-center border-round-lg flex-shrink-0"
                                style={{
                                    width: 44, height: 44,
                                    background: 'linear-gradient(135deg, #10b981, #1bb8a7)',
                                    boxShadow: '0 4px 14px rgba(16,185,129,0.4)',
                                }}
                            >
                                <i className="pi pi-file-pdf" style={{ fontSize: '1.15rem', color: '#fff' }} />
                            </div>
                            <div className="flex flex-column gap-0" style={{ minWidth: 0 }}>
                                <p
                                    className="m-0 font-bold"
                                    style={{
                                        fontSize: '0.88rem',
                                        color: 'var(--text-color)',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: '280px',
                                    }}
                                >
                                    {file.name}
                                </p>
                                <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>
                                    {(file.size / 1024).toFixed(1)} KB
                                </span>
                            </div>
                        </div>

                        <button
                            onClick={handleRemove}
                            title="Remove file"
                            style={{
                                width: 30, height: 30,
                                borderRadius: '50%',
                                border: '1px solid #ef444440',
                                background: '#fee2e2',
                                color: '#ef4444',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                transition: 'all 0.2s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#fca5a5'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fee2e2'}
                        >
                            <i className="pi pi-times" style={{ fontSize: '0.65rem' }} />
                        </button>
                    </div>
                )}
            </div>

            {/* Hidden file input */}
            <input
                ref={inputRef}
                type="file"
                accept="application/pdf"
                style={{ display: 'none' }}
                onChange={handleChange}
            />

            {/* Error message */}
            {error && (
                <div
                    className="flex align-items-center gap-2 border-round-lg px-3 py-2"
                    style={{
                        background: '#fee2e2',
                        border: '1px solid #ef444430',
                        color: '#dc2626',
                        fontSize: '0.8rem',
                        maxWidth: '560px',
                        width: '100%',
                        boxSizing: 'border-box',
                    }}
                >
                    <i className="pi pi-exclamation-circle" style={{ fontSize: '0.85rem', flexShrink: 0 }} />
                    <span>{error}</span>
                </div>
            )}

            {/* Submit button */}
            <button
                disabled={!file || loading}
                style={{
                    background: !file || loading
                        ? '#d1d5db'
                        : 'linear-gradient(135deg, #10b981, #1bb8a7)',
                    color: !file || loading ? '#9ca3af' : '#fff',
                    border: 'none',
                    borderRadius: '0.75rem',
                    padding: '0.75rem 2.5rem',
                    fontSize: '0.92rem',
                    fontWeight: 700,
                    cursor: !file || loading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    boxShadow: !file || loading
                        ? 'none'
                        : '0 4px 18px rgba(16,185,129,0.4)',
                    transition: 'all 0.25s',
                    minWidth: '200px',
                    justifyContent: 'center',
                }}
                onMouseEnter={e => {
                    if (file && !loading) e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
                // submit is handled by parent via onFileSelect + explicit button click in page
                form="warranty-upload-form"
                type="submit"
            >
                {loading ? (
                    <>
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.9rem' }} />
                        Checking warranty…
                    </>
                ) : (
                    <>
                        <i className="pi pi-shield" style={{ fontSize: '0.9rem' }} />
                        Check Warranty
                    </>
                )}
            </button>

            {/* Helper note */}
            <p className="m-0 text-center text-500" style={{ fontSize: '0.75rem', maxWidth: '420px' }}>
                <i className="pi pi-lock mr-1" style={{ fontSize: '0.7rem' }} />
                Your invoice data is processed securely and never stored.
                Supports Amazon & Flipkart invoices only.
            </p>
        </div>
    );
};
